import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../lib/supabase';
import type { HealthCheck, HostHealth, Machine } from '../lib/database.types';

interface TileData {
  key: string;
  type: string;
  slug: string;
  label: string;
  status: HostHealth['status'];
  detail: string;
  machine?: Machine;
  staleMinutes?: number;
}

// Machine is considered stale (effectively offline) if its last heartbeat
// is older than this many minutes. The factory-agent on forge-01 writes
// every minute under normal operation, so 10 min is plenty of slack for
// transient hiccups while still catching dead agents within a single sync
// window.
const STALE_HEARTBEAT_MINUTES = 10;

function machineEffectiveStatus(m: Machine): {
  status: HostHealth['status'];
  staleMinutes?: number;
} {
  if (!m.last_heartbeat) {
    // Never heartbeated → unknown, not online.
    return { status: 'unknown' };
  }
  const ageMin = (Date.now() - new Date(m.last_heartbeat).getTime()) / 60_000;
  if (ageMin > STALE_HEARTBEAT_MINUTES) {
    // Stored status is lying — agent isn't writing heartbeats. Treat as down.
    return { status: 'down', staleMinutes: Math.round(ageMin) };
  }
  if (m.status === 'online') return { status: 'ok' };
  if (m.status === 'offline') return { status: 'down' };
  return { status: 'unknown' };
}

export function UptimeView() {
  const [machines, setMachines] = useState<Machine[]>([]);
  const [health, setHealth] = useState<HostHealth[]>([]);
  const [history, setHistory] = useState<HealthCheck[]>([]);
  const [selectedTile, setSelectedTile] = useState<TileData | null>(null);

  useEffect(() => {
    const load = async () => {
      const since = new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString();
      const [{ data: m }, { data: h }, { data: hist }] = await Promise.all([
        supabase.from('factory_machines').select('*'),
        supabase.from('tb_host_health').select('*'),
        supabase
          .from('tb_health_checks')
          .select('*')
          .gte('checked_at', since)
          .order('checked_at', { ascending: false })
          .limit(500),
      ]);
      setMachines((m as Machine[] | null) ?? []);
      setHealth((h as HostHealth[] | null) ?? []);
      setHistory((hist as HealthCheck[] | null) ?? []);
    };
    load();
  }, []);

  const tiles: TileData[] = useMemo(() => {
    const out: TileData[] = [];
    for (const m of machines) {
      const h = health.find((x) => x.target_type === 'machine' && x.target_slug === m.name);
      // Prefer probe-based health if we have it; otherwise derive from heartbeat
      // age so a stale agent shows down even though the stored status row says
      // online.
      const eff = machineEffectiveStatus(m);
      const status: HostHealth['status'] = h?.status ?? eff.status;
      const detail =
        eff.staleMinutes !== undefined
          ? `${m.role}${m.zone ? ' · ' + m.zone : ''} · stale ${eff.staleMinutes}m`
          : `${m.role}${m.zone ? ' · ' + m.zone : ''}`;
      out.push({
        key: `machine:${m.name}`,
        type: 'machine',
        slug: m.name,
        label: m.name,
        status,
        detail,
        machine: m,
        staleMinutes: eff.staleMinutes,
      });
    }
    for (const h of health) {
      if (h.target_type === 'machine') continue;
      out.push({
        key: `${h.target_type}:${h.target_slug}`,
        type: h.target_type,
        slug: h.target_slug,
        label: h.target_slug,
        status: h.status,
        detail: h.probe_url ?? '',
      });
    }
    return out;
  }, [machines, health]);

  const selectedHistory = useMemo(() => {
    if (!selectedTile) return [];
    return history
      .filter(
        (h) => h.target_type === selectedTile.type && h.target_slug === selectedTile.slug,
      )
      .slice(0, 60);
  }, [history, selectedTile]);

  return (
    <div className="flex-1 overflow-auto">
      <div className="max-w-5xl p-8 space-y-6">
        <header>
          <h1 className="text-3xl font-semibold tracking-tight">Uptime</h1>
          <p className="text-sm text-muted font-mono mt-1">
            {tiles.length} targets · {history.length} checks in 24h
          </p>
        </header>
        {tiles.length === 0 && (
          <div className="panel p-10 text-center text-dim font-mono text-sm">
            no health checks yet — deploy the cloudflare worker
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {tiles.map((t) => (
            <button
              key={t.key}
              onClick={() => setSelectedTile(t)}
              className="panel p-4 space-y-2 text-left hover:border-amber/60 hover:bg-panelHi/40 transition-colors"
            >
              <div className="flex items-center gap-2">
                <span className={`status-dot status-${t.status}`} />
                <span className="font-mono text-sm truncate">{t.label}</span>
              </div>
              {t.detail && <div className="text-xs text-dim font-mono truncate">{t.detail}</div>}
              <div className="text-xs font-mono text-muted uppercase">{t.status}</div>
            </button>
          ))}
        </div>
      </div>

      {selectedTile && (
        <TileDrawer
          tile={selectedTile}
          history={selectedHistory}
          onClose={() => setSelectedTile(null)}
        />
      )}
    </div>
  );
}

function TileDrawer({
  tile,
  history,
  onClose,
}: {
  tile: TileData;
  history: HealthCheck[];
  onClose: () => void;
}) {
  const okCount = history.filter((h) => h.status === 'ok').length;
  const avgLatency = history.length
    ? Math.round(
        history.reduce((sum, h) => sum + (h.latency_ms ?? 0), 0) /
          history.filter((h) => h.latency_ms != null).length,
      )
    : 0;
  const uptimePct = history.length ? Math.round((okCount / history.length) * 100) : null;

  return (
    <>
      <div className="fixed inset-0 bg-black/60 z-40 backdrop-blur-sm" onClick={onClose} />
      <aside className="fixed right-0 top-0 bottom-0 w-full sm:w-[540px] z-50 bg-panel border-l border-border shadow-2xl flex flex-col">
        <header className="flex items-center justify-between p-4 border-b border-border">
          <div>
            <div className="text-xs font-mono text-dim uppercase tracking-widest">{tile.type}</div>
            <h2 className="text-xl font-semibold mt-0.5">{tile.label}</h2>
          </div>
          <button onClick={onClose} className="btn text-xs">
            close
          </button>
        </header>
        <div className="flex-1 overflow-y-auto p-5 space-y-5">
          <div className="grid grid-cols-3 gap-2">
            <div className="panel-hi p-3 rounded">
              <div className="text-[10px] font-mono text-dim uppercase tracking-widest">
                status
              </div>
              <div
                className={
                  'text-2xl font-semibold tabular-nums mt-1 uppercase ' +
                  (tile.status === 'ok'
                    ? 'text-green'
                    : tile.status === 'down'
                      ? 'text-red'
                      : 'text-amberHi')
                }
              >
                {tile.status}
              </div>
            </div>
            {uptimePct !== null && (
              <div className="panel-hi p-3 rounded">
                <div className="text-[10px] font-mono text-dim uppercase tracking-widest">
                  24h ok
                </div>
                <div className="text-2xl font-semibold tabular-nums mt-1 text-green">
                  {uptimePct}%
                </div>
              </div>
            )}
            {avgLatency > 0 && (
              <div className="panel-hi p-3 rounded">
                <div className="text-[10px] font-mono text-dim uppercase tracking-widest">
                  avg ms
                </div>
                <div className="text-2xl font-semibold tabular-nums mt-1 text-ink">
                  {avgLatency}
                </div>
              </div>
            )}
          </div>

          {tile.machine && (
            <div className="space-y-2">
              <div className="text-xs font-mono text-dim uppercase tracking-widest">Machine</div>
              <div className="panel-hi p-3 rounded space-y-1 text-xs font-mono">
                <Row label="role" value={tile.machine.role} />
                {tile.machine.zone && <Row label="zone" value={tile.machine.zone} />}
                {tile.machine.external_ip && (
                  <Row label="ip" value={tile.machine.external_ip} />
                )}
                {tile.machine.last_heartbeat && (
                  <Row
                    label="heartbeat"
                    value={new Date(tile.machine.last_heartbeat).toLocaleString()}
                  />
                )}
              </div>
            </div>
          )}

          <div className="space-y-2">
            <div className="text-xs font-mono text-dim uppercase tracking-widest">
              Recent checks ({history.length})
            </div>
            {history.length === 0 && (
              <div className="text-xs text-dim font-mono italic">no checks yet</div>
            )}
            <div className="space-y-1">
              {history.map((h) => (
                <div
                  key={h.id}
                  className="flex items-center gap-2 text-xs font-mono p-2 panel-hi rounded"
                >
                  <span className={`status-dot status-${h.status} shrink-0`} />
                  <span className="text-muted w-16">{h.status}</span>
                  <span className="text-dim flex-1">
                    {new Date(h.checked_at).toLocaleString()}
                  </span>
                  {h.latency_ms != null && (
                    <span className="text-ink">{h.latency_ms}ms</span>
                  )}
                  {h.http_code && <span className="text-dim">{h.http_code}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-2">
      <span className="text-dim w-20">{label}</span>
      <span className="text-ink">{value}</span>
    </div>
  );
}
